import React, { useMemo, useState, useEffect } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import FormHelperText from '@material-ui/core/FormHelperText'
import Paper from '@material-ui/core/Paper'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import MenuItem from '@material-ui/core/MenuItem'
import { IOfferingResponse } from 'types/api/temple'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { deityListThunk, selectCurrentTempleDeities } from 'store/reducers/temple/deitySlice'
import { offeringCategoryListThunk, selectCurrentTempleOfferingCategories } from 'store/reducers/temple/offeringCategorySlice'

interface OfferingCheckboxListProps {
    offerings: IOfferingResponse[]
    selectedOfferings: (string | number)[]
    onChange: (offerings: (string | number)[]) => void
    disabledOfferingValues?: (string | number)[]
    errorText?: string
}

const getOfferingValue = (offering: IOfferingResponse) => offering.url

const isSameOfferingValue = (value: string | number, compareValue: string | number, offerings: IOfferingResponse[]) => {
    if (value === compareValue || String(value) === String(compareValue)) return true
    const offering = offerings.find(item => item.url === value || String(item.id) === String(value))
    return Boolean(offering && (offering.url === compareValue || String(offering.id) === String(compareValue)))
}

const OfferingCheckboxList: React.FC<OfferingCheckboxListProps> = ({
    offerings,
    selectedOfferings,
    onChange,
    disabledOfferingValues = [],
    errorText
}) => {
    const [search, setSearch] = useState('')
    const [selectedDeity, setSelectedDeity] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('')

    const dispatch = useAppDispatch()
    const deities = useAppSelector(selectCurrentTempleDeities)
    const categories = useAppSelector(selectCurrentTempleOfferingCategories)

    useEffect(() => {
        if (deities.length === 0) {
            dispatch(deityListThunk({}))
        }
        if (categories.length === 0) {
            dispatch(offeringCategoryListThunk({}))
        }
    }, [dispatch, deities.length, categories.length])

    const filteredOfferings = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase()
        return offerings.filter(offering => {
            if (normalizedSearch && !offering.name.toLowerCase().includes(normalizedSearch)) {
                return false
            }
            if (selectedCategory && offering.category !== selectedCategory) {
                return false
            }
            if (selectedDeity && !offering.assignedDeities.includes(selectedDeity)) {
                return false
            }
            return true
        })
    }, [offerings, search, selectedCategory, selectedDeity])

    const isDisabled = (offering: IOfferingResponse) =>
        disabledOfferingValues.some(value => isSameOfferingValue(value, getOfferingValue(offering), offerings))

    const selectableVisibleValues: (string | number)[] = filteredOfferings
        .filter(offering => !isDisabled(offering))
        .map(getOfferingValue)

    const selectedVisibleCount = selectableVisibleValues.filter(value => selectedOfferings.includes(value)).length
    const allVisibleSelected = selectableVisibleValues.length > 0 && selectedVisibleCount === selectableVisibleValues.length
    const hasPartialVisibleSelection = selectedVisibleCount > 0 && selectedVisibleCount < selectableVisibleValues.length

    const handleToggle = (value: string | number) => {
        if (selectedOfferings.includes(value)) {
            onChange(selectedOfferings.filter(selected => selected !== value))
        } else {
            onChange([...selectedOfferings, value])
        }
    }

    const handleToggleAllVisible = () => {
        if (allVisibleSelected) {
            onChange(selectedOfferings.filter(selected => !selectableVisibleValues.includes(selected)))
        } else {
            const mergedValues = [...selectedOfferings]
            selectableVisibleValues.forEach(value => {
                if (!mergedValues.includes(value)) mergedValues.push(value)
            })
            onChange(mergedValues)
        }
    }

    const handleClear = () => onChange([])

    return (
        <Box mt={2} mb={2}>
            <Paper variant="outlined" style={{ width: '100%' }}>
                <Box
                    px={2}
                    py={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    style={{ gap: 8 }}
                >
                    <Typography variant="subtitle2">Offerings</Typography>
                    <Box display="flex" flex="1 1 auto" style={{ gap: 8, flexWrap: 'wrap' }} alignItems="center">
                        <Box flex="1 1 200px" maxWidth={300}>
                            <TextField
                                value={search}
                                onChange={event => setSearch(event.target.value)}
                                placeholder="Search offerings"
                                variant="outlined"
                                size="small"
                                fullWidth
                            />
                        </Box>
                        <Box flex="1 1 150px" maxWidth={250}>
                            <TextField
                                select
                                value={selectedDeity}
                                onChange={event => setSelectedDeity(event.target.value as string)}
                                size="small"
                                label="Deity"
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Deities</MenuItem>
                                {deities.map(deity => (
                                    <MenuItem key={deity.id} value={deity.url}>
                                        {deity.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box flex="1 1 150px" maxWidth={250}>
                            <TextField
                                select
                                value={selectedCategory}
                                onChange={event => setSelectedCategory(event.target.value as string)}
                                size="small"
                                label="Category"
                                variant="outlined"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            >
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(category => (
                                    <MenuItem key={category.id} value={category.url}>
                                        {category.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Checkbox
                            checked={allVisibleSelected}
                            indeterminate={hasPartialVisibleSelection}
                            onChange={handleToggleAllVisible}
                            disabled={selectableVisibleValues.length === 0}
                            size="small"
                        />
                        <Typography variant="body2">Select visible</Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2">{selectedOfferings.length} selected</Typography>
                        <Button size="small" color="primary" onClick={handleClear} disabled={selectedOfferings.length === 0}>
                            Clear
                        </Button>
                    </Box>
                </Box>
                <Box
                    px={2}
                    py={1}
                    display="grid"
                    gridTemplateColumns="repeat(auto-fit, minmax(260px, 1fr))"
                    gridAutoRows="minmax(32px, auto)"
                    style={{ maxHeight: '55vh', minHeight: 360, overflow: 'auto', columnGap: 12 }}
                >
                    {filteredOfferings.map(offering => {
                        const value = getOfferingValue(offering)
                        const disabled = isDisabled(offering)
                        const isSelected = selectedOfferings.includes(value)
                        return (
                            <Box
                                key={offering.id}
                                display="flex"
                                alignItems="center"
                                minWidth={0}
                                px={0.5}
                                onClick={() => !disabled && handleToggle(value)}
                                style={{
                                    cursor: disabled ? 'default' : 'pointer',
                                    opacity: disabled ? 0.55 : 1,
                                    borderRadius: 4
                                }}
                            >
                                <Checkbox
                                    checked={isSelected}
                                    tabIndex={-1}
                                    disableRipple
                                    disabled={disabled}
                                    size="small"
                                    style={{ padding: 4, marginRight: 4 }}
                                    inputProps={{ 'aria-label': offering.name }}
                                />
                                <Typography variant="body2" noWrap title={offering.name}>
                                    {offering.name}
                                    {disabled && (
                                        <Typography component="span" variant="caption" color="textSecondary">
                                            {' '}· Already assigned
                                        </Typography>
                                    )}
                                </Typography>
                            </Box>
                        )
                    })}
                    {filteredOfferings.length === 0 && (
                        <Typography variant="body2" color="textSecondary">No offerings found</Typography>
                    )}
                </Box>
            </Paper>
            {errorText && <FormHelperText error>{errorText}</FormHelperText>}
        </Box>
    )
}

export default OfferingCheckboxList
